import { c as _c } from "react/compiler-runtime";
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { $convertToMarkdownString, $convertFromMarkdownString } from '@lexical/markdown';
import SAutoLinkPlugin from './lexical/AutoLinkPlugin';
import { StyleProps } from 'components';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { COMMAND_PRIORITY_HIGH, CLEAR_EDITOR_COMMAND, CLEAR_HISTORY_COMMAND, LexicalEditor, KEY_ENTER_COMMAND } from 'lexical';
import { ToolbarPlugin } from './lexical/ToolbarPlugin';
import { BlockSelectPlugin } from './lexical/BlockSelect';
import { useContext, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { useDynamicCallback } from 'components/hooks/useDynamicCallback';
import { delay } from 'shared/promise';
import { ErrorBoundary } from 'react-error-boundary';
import { QuestionContext } from 'components/question/questionComponentTypes';
import TreeViewPlugin from './lexical/TreeViewPlugin';
import { MentionNode } from './lexical/MentionNode';
import { MentionPlugin } from './lexical/MentionPlugin';
import { ImageNode, ImagePlugin } from './lexical/image';
import { DraggableBlockPlugin } from './lexical/DraggableBlockPlugin';
import { useRefState } from 'components/hooks';
import { omit } from 'remeda';
import styles from './RichText.module.css';
import { transformers } from './lexical/MarkDownTransformers';
import { twMerge } from 'tailwind-merge';
const {
  debug: consoleDebug
} = console;
export const editorCmd = (editor: LexicalEditor) => {
  const getMd = () => {
    let markdown: string = '';
    editor.getEditorState().read(() => {
      markdown = $convertToMarkdownString(transformers);
    });
    return markdown;
  };
  const setMd = (text: string) => {
    editor.update(() => {
      $convertFromMarkdownString(text, transformers);
    }, {
      discrete: true
    });
  };
  const clear = () => {
    editor.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined);
    editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
  };
  return {
    getMd,
    setMd,
    clear,
    editor
  };
};
export type EditorCmd = ReturnType<typeof editorCmd>;
export const RichText = (props: {
  onChange?: (md: string) => void;
  readonly?: boolean;
  disabled?: boolean;
  placeholder?: React.ReactNode;
  reset?: () => void;
  mentionContext?: {
    question_id: string;
  };
  debugName?: string;
  disableHeightExpand?: boolean;
} & StyleProps & ComponentPluginProps) => {
  const {
    onChange,
    className,
    style,
    placeholder,
    children,
    mentionContext,
    debugName,
    disabled,
    disableHeightExpand,
    ...componentProps
  } = props;
  const {
    value
  } = componentProps;
  const readonly = props.readonly || disabled;
  const extraProps = omit(componentProps, ['readonly', 'reset', 'value', 'autoFocus', 'syncValue', 'onKeyDown', 'onBlur', 'debugTitle']);
  const {
    element: contentEl,
    ref: contentRef
  } = useRefState<HTMLDivElement>();
  const {
    element: sizeRootEl,
    ref: sizeRootRef
  } = useRefState<HTMLDivElement>();
  const questionCtx = useContext(QuestionContext);
  const [customHeight, setCustomHeight] = useState<number | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  useEffect(() => {
    if (sizeRootEl) {
      ;
      (sizeRootEl as any).setMd = (md: string) => {
        onChange?.(md);
      };
    }
  }, [sizeRootEl]);

  // Handle drag and drop for images
  useEffect(() => {
    if (!contentEl || readonly) return;
    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer?.types.includes('Files')) {
        setIsDragOver(true);
      }
    };
    const handleDragLeave = (e_0: DragEvent) => {
      e_0.preventDefault();
      e_0.stopPropagation();
      // Only set drag over to false if we're leaving the content area
      if (!contentEl.contains(e_0.relatedTarget as Node)) {
        setIsDragOver(false);
      }
    };
    const handleDrop = (e_1: DragEvent) => {
      e_1.preventDefault();
      e_1.stopPropagation();
      setIsDragOver(false);
    };
    contentEl.addEventListener('dragover', handleDragOver);
    contentEl.addEventListener('dragleave', handleDragLeave);
    contentEl.addEventListener('drop', handleDrop);
    return () => {
      contentEl.removeEventListener('dragover', handleDragOver);
      contentEl.removeEventListener('dragleave', handleDragLeave);
      contentEl.removeEventListener('drop', handleDrop);
    };
  }, [contentEl, readonly]);
  return <div ref={sizeRootRef} {...extraProps} className={twMerge(styles.root, 'relative w-full group', readonly ? null : 'ml-[-3px] pb-1', disabled ? 'bg-gray-100 opacity-25' : null, readonly ? '' : 'border-y border-gray-500/10', isDragOver ? 'border-2 border-dashed border-primary bg-primary/5' : '', className)}>
      <div ref={contentRef} style={{
      ...style,
      ...(customHeight && !disableHeightExpand && !readonly ? {
        minHeight: `${customHeight}px`
      } : {})
    }} className={twMerge(!disableHeightExpand ? 'overflow-auto' : '')}>
        {contentEl && <ErrorBoundary onError={e_2 => console.error('rich text error', e_2)} FallbackComponent={({
        error
      }) => <div>{error.message}</div>}>
            <LexicalComposer initialConfig={{
          namespace: 'RichText',
          onError: e_3 => {
            console.error('RichText', e_3);
          },
          nodes: [MentionNode, HeadingNode, QuoteNode, CodeNode, CodeHighlightNode, AutoLinkNode, LinkNode, ListNode, ListItemNode, ImageNode],
          editorState: editor => {
            if (value) {
              $convertFromMarkdownString(value, transformers);
            }
          },
          theme: {
            text: {
              underline: 'underline',
              strikethrough: 'line-through',
              underlineStrikethrough: 'underline line-through'
            }
          },
          editable: !readonly
        }}>
              <div className='flex items-end'>
                <RichTextPlugin ErrorBoundary={LexicalErrorBoundary} contentEditable={<ContentEditable className={`h-full w-full space-y-2 select-text outline-none ${readonly ? 'cursor-default' : 'cursor-text pt-1 text-[15px] pl-[16px]'}`} readOnly={readonly} id='content' />} placeholder={!readonly ? <div className='pointer-events-none absolute left-[1.2em] top-[0.4em] text-[--mantine-color-placeholder] opacity-30'>
                        {placeholder}
                      </div> : <></>} />
                <ComponentPlugin readonly={readonly ?? false} {...componentProps} syncValue>
                  {children}
                </ComponentPlugin>
              </div>
              {readonly ? <></> : <>
                  <OnChangePlugin onChange={e_4 => {
              if (onChange) {
                e_4.read(() => {
                  const md_0 = $convertToMarkdownString(transformers);
                  if (debugName) consoleDebug('Markdown changed:', debugName, md_0);
                  if (md_0 != value) onChange(md_0);
                });
              }
            }} />
                  <ToolbarPlugin />
                  <MarkdownShortcutPlugin transformers={transformers} />
                  <HistoryPlugin />
                  <SAutoLinkPlugin />
                  <TabIndentationPlugin />
                  <BlockSelectPlugin debugName={debugName} />
                  {questionCtx ? <MentionPlugin context={questionCtx} /> : <></>}
                  {debugName ? <TreeViewPlugin /> : <></>}
                  <DraggableBlockPlugin anchorElem={contentEl ?? undefined} />
                  <ClearEditorPlugin />
                </>}
              <LinkPlugin />
              <ListPlugin />
              <ImagePlugin />
            </LexicalComposer>
          </ErrorBoundary>}

        {/* Drag and drop overlay */}
        {isDragOver && !readonly && <div className='absolute inset-0 bg-primary/10 border-2 border-dashed border-primary rounded-lg flex items-center justify-center z-10'>
            <div className='text-center text-primary font-semibold'>
              <div className='text-2xl mb-2'>📷</div>
              <div>Drop images here to upload</div>
            </div>
          </div>}
      </div>
      {!disableHeightExpand && !readonly && sizeRootEl && <ResizeHandle onResize={(startHeight, deltaY) => {
      setCustomHeight(Math.max(12, startHeight + deltaY));
    }} onStart={() => sizeRootEl.clientHeight} disabled={disabled} />}
    </div>;
};
type ComponentPluginProps = {
  value?: string;
  syncValue?: boolean;
  readonly?: boolean;
  autoFocus?: boolean;
  children?: (editor: EditorCmd) => React.ReactNode;
  onKeyDown?: (editor: EditorCmd, event: globalThis.KeyboardEvent) => void;
  onBlur?: (editor: EditorCmd) => void;
  debugTitle?: string;
};
const ComponentPlugin = t0 => {
  const $ = _c(27);
  const {
    children,
    onKeyDown,
    onBlur,
    readonly,
    value,
    autoFocus,
    syncValue
  } = t0;
  const [rawEditor] = useLexicalComposerContext();
  let t1;
  if ($[0] !== rawEditor) {
    t1 = editorCmd(rawEditor);
    $[0] = rawEditor;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const editor = t1;
  const onKeyDynamic = useDynamicCallback(onKeyDown ?? _temp);
  const onBlurDynamic = useDynamicCallback(onBlur ?? _temp2);
  const [initialValueState, setInitialValue] = useState(value);
  let t2;
  if ($[2] !== editor || $[3] !== onBlurDynamic || $[4] !== onKeyDynamic || $[5] !== rawEditor) {
    t2 = () => {
      const onKey = e => {
        onKeyDynamic(editor, e);
      };
      const onBlur_0 = () => {
        onBlurDynamic(editor);
      };
      rawEditor.registerCommand(KEY_ENTER_COMMAND, _temp3, COMMAND_PRIORITY_HIGH);
      return rawEditor.registerRootListener((root, prev) => {
        if (prev) {
          prev.removeEventListener("keydown", onKey);
          prev.removeEventListener("blur", onBlur_0);
        }
        if (root) {
          root.addEventListener("keydown", onKey);
          root.addEventListener("blur", onBlur_0);
        }
      });
    };
    $[2] = editor;
    $[3] = onBlurDynamic;
    $[4] = onKeyDynamic;
    $[5] = rawEditor;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== rawEditor) {
    t3 = [rawEditor];
    $[7] = rawEditor;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[9] !== autoFocus || $[10] !== rawEditor || $[11] !== readonly) {
    t4 = () => {
      const editorEditable = rawEditor.isEditable();
      const intendedEditable = !readonly;
      if (editorEditable != intendedEditable) {
        rawEditor.setEditable(intendedEditable);
      }
      if (intendedEditable && autoFocus) {
        delay(50).then(() => {
          const activeElement = document.activeElement;
          const rootElement = rawEditor.getRootElement() as HTMLDivElement;
          if (!rootElement) {
            return;
          }
          if (!rootElement.contains(activeElement) || rootElement !== null || activeElement === null) {
            rawEditor.focus(undefined, {
              defaultSelection: "rootEnd"
            });
            delay(100).then(() => rootElement.focus({
              preventScroll: true
            }));
          }
        });
      }
    };
    t5 = [rawEditor, readonly, autoFocus];
    $[9] = autoFocus;
    $[10] = rawEditor;
    $[11] = readonly;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  if ($[14] !== editor || $[15] !== initialValueState || $[16] !== syncValue || $[17] !== value) {
    t6 = () => {
      if (initialValueState != value && value != null) {
        setInitialValue(value);
        const editorMd = editor.getMd();
        if (syncValue && editorMd != value) {
          editor.setMd(value);
        }
      }
    };
    $[14] = editor;
    $[15] = initialValueState;
    $[16] = syncValue;
    $[17] = value;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== rawEditor || $[20] !== value) {
    t7 = [rawEditor, value];
    $[19] = rawEditor;
    $[20] = value;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  useEffect(t6, t7);
  let t8;
  if ($[22] !== children || $[23] !== editor) {
    t8 = children?.(editor);
    $[22] = children;
    $[23] = editor;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t8) {
    t9 = <>{t8}</>;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
};
const ResizeHandle = t0 => {
  const $ = _c(13);
  const {
    onResize,
    onStart,
    disabled
  } = t0;
  const start = useRef(null);
  const startY = useRef(null);
  const [active, setActive] = useState(false);
  let t1;
  if ($[0] !== disabled || $[1] !== onResize || $[2] !== onStart) {
    t1 = e => {
      if (disabled) {
        return;
      }
      e.preventDefault();
      start.current = onStart();
      startY.current = e.clientY;
      setActive(true);
      const handleMouseMove = e_0 => {
        if (startY.current != null && start.current != null) {
          const deltaY = e_0.clientY - startY.current;
          onResize(start.current, deltaY);
        }
      };
      const handleMouseUp = () => {
        start.current = null;
        startY.current = null;
        setActive(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };
    $[0] = disabled;
    $[1] = onResize;
    $[2] = onStart;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleMouseDown = t1;
  let t2;
  if ($[4] !== disabled || $[5] !== onResize || $[6] !== onStart) {
    t2 = e_1 => {
      if (disabled) {
        return;
      }
      e_1.preventDefault();
      const currentStart = onStart();
      onResize(currentStart, -1000);
    };
    $[4] = disabled;
    $[5] = onResize;
    $[6] = onStart;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleDoubleClick = t2;
  let t3;
  if ($[8] !== active || $[9] !== disabled || $[10] !== handleDoubleClick || $[11] !== handleMouseDown) {
    t3 = disabled ? null : <div onMouseDown={handleMouseDown} onDoubleClick={handleDoubleClick} className={twMerge(styles.resizeHandle, "absolute bottom-0 translate-y-1/2 left-1/2 transform -translate-x-1/2 w-12 h-2 bg-primary border-primary rounded-full", "cursor-ns-resize transition-colors bg-opacity-0 group-hover:bg-opacity-10 hover:!bg-opacity-100", active && "bg-opacity-100")} />;
    $[8] = active;
    $[9] = disabled;
    $[10] = handleDoubleClick;
    $[11] = handleMouseDown;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
};
function _temp() {}
function _temp2() {}
function _temp3(event) {
  return event?.metaKey == true || event?.ctrlKey == true;
}